import {
  BMI_RANGE,
  getBMI,
  HEIGHT_FEET_RANGE,
  HEIGHT_INCHES_RANGE,
  isBmiValid,
  isHeightFeetValid,
  isHeightInchesValid,
  isWeightLbsValid,
  MeasurementRange,
  WEIGHT_LBS_RANGE,
} from '../../../util/validations';
import { ReferralBmiSource } from '../../../interfaces/referral';

/** The measurement inputs on the referral card, as the inputs produce them. */
export interface MeasurementEditState {
  heightFeet: string;
  heightInch: string;
  weightLb: string;
  bmi: string;
  /**
   * Whether `bmi` currently follows height and weight. Only a following BMI is rewritten when
   * they change. Editing the BMI field stops it following, even down to empty: an emptied field
   * stays empty. Following again takes the explicit `followCalculatedBmi`.
   */
  bmiDerived: boolean;
  /**
   * What a following BMI shows while height and weight give none, instead of emptying. Set
   * only for a BMI leads-api estimated (an ICD-10 code, or a BMI range or open bound the
   * document writes; see `isEstimatedBmiSource`): the estimate stands until the measurements
   * give a BMI, which then replaces it, as the server does on save. Typing in the BMI field
   * drops it.
   */
  bmiFallback?: string;
}

/** The inputs a BMI is derived from. */
export type MeasurementKey = 'heightFeet' | 'heightInch' | 'weightLb';

/** Every measurement input that is range-checked before a save. */
export type ValidatedMeasurementKey = MeasurementKey | 'bmi';

const MEASUREMENT_KEYS: MeasurementKey[] = ['heightFeet', 'heightInch', 'weightLb'];

const between = ({ min, max }: MeasurementRange, unit?: string) =>
  unit ? `Between ${min} and ${max} ${unit}` : `Between ${min} and ${max}`;

/**
 * The manual intake form's validators (`util/validations`), reused rather than restated, with
 * messages built from the same range constants the validators use. leads-api only checks that
 * each is a number.
 */
const RULES: Record<
  ValidatedMeasurementKey,
  { valid: (value: string) => boolean; message: string; label: string }
> = {
  heightFeet: {
    valid: isHeightFeetValid,
    message: between(HEIGHT_FEET_RANGE, 'ft'),
    label: 'Height (ft)',
  },
  heightInch: {
    valid: isHeightInchesValid,
    message: between(HEIGHT_INCHES_RANGE, 'in'),
    label: 'Height (in)',
  },
  weightLb: {
    valid: isWeightLbsValid,
    message: between(WEIGHT_LBS_RANGE, 'lb'),
    label: 'Weight (lb)',
  },
  bmi: { valid: isBmiValid, message: between(BMI_RANGE), label: 'BMI' },
};

/** Why a typed measurement cannot be saved, or `null` when it is blank or valid. */
export const measurementError = (key: ValidatedMeasurementKey, value: string): string | null => {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') {
    return null;
  }
  return Number.isFinite(Number(trimmed)) && RULES[key].valid(trimmed) ? null : RULES[key].message;
};

/**
 * What blocks saving these measurements, or `null`. A value typed but rejected used to be
 * dropped silently: `-3` saved as "success" with nothing changed. The card shows it from the
 * moment the edit opens, so a pre-filled value out of range is visible before Save.
 */
export const measurementProblem = (
  state: Pick<MeasurementEditState, ValidatedMeasurementKey>,
): string | null => {
  const invalid = (Object.keys(RULES) as ValidatedMeasurementKey[]).filter(
    (key) => measurementError(key, state[key]) !== null,
  );
  return invalid.length
    ? `Fix ${invalid.map((key) => RULES[key].label).join(', ')} before saving: ${invalid
        .map((key) => RULES[key].message.toLowerCase())
        .join('; ')}.`
    : null;
};

/**
 * What `getBMI` gives for these measurements, whatever its range — or `null` unless all three
 * are filled in and valid. Inches are required, as in the form: treating a blank as 0 put 46.87
 * on a 5 ft 4 in, 240 lb patient whose BMI is 41.19.
 */
export const calculatedBmi = (
  state: Pick<MeasurementEditState, MeasurementKey>,
): number | null => {
  const unusable = (key: MeasurementKey) =>
    String(state[key] ?? '').trim() === '' || measurementError(key, state[key]) !== null;
  if (MEASUREMENT_KEYS.some(unusable)) {
    return null;
  }
  return getBMI({
    feet: Number(state.heightFeet),
    inches: Number(state.heightInch),
    weight: Number(state.weightLb),
  });
};

/**
 * The BMI the card may derive from height and weight: `calculatedBmi`, held to the same
 * `BMI_RANGE` as a typed BMI, or `null`. The height and weight bounds allow combinations far
 * outside it (5 ft at 600 lb gives 117; 1 ft at 999 lb gives 4877), and a derived value that
 * failed the BMI check blocked the whole card's save, with "Use calculated BMI" inserting the
 * same invalid value. Out of range, nothing is derived: the BMI is left as it is and the field
 * says what the measurements give (`calculatedBmiOutOfRange`), so the operator checks them.
 */
export const bmiFromMeasurements = (
  state: Pick<MeasurementEditState, MeasurementKey>,
): number | null => {
  const bmi = calculatedBmi(state);
  return bmi !== null && measurementError('bmi', String(bmi)) === null ? bmi : null;
};

/** What the measurements give when it is outside `BMI_RANGE`, or `null`. */
export const calculatedBmiOutOfRange = (
  state: Pick<MeasurementEditState, MeasurementKey>,
): number | null => {
  const bmi = calculatedBmi(state);
  return bmi !== null && bmiFromMeasurements(state) === null ? bmi : null;
};

/** The BMI range, as the card states it. */
export const BMI_RANGE_TEXT = `${BMI_RANGE.min}–${BMI_RANGE.max}`;

/** Whether the loaded BMI is empty or exactly what the loaded measurements derive. */
const matchesMeasurements = (state: Omit<MeasurementEditState, 'bmiDerived'>): boolean => {
  if (state.bmi.trim() === '') {
    return true;
  }
  const derived = bmiFromMeasurements(state);
  return derived !== null && derived === Number(state.bmi);
};

/**
 * Whether leads-api estimated the BMI rather than read or derived it: from an ICD-10 Z68 code
 * (`icd10`), the midpoint of a BMI range the document writes (`stated_range`), or an open bound
 * it writes (`stated_open_range`). An estimate stands until height and weight give a BMI, the
 * server restores it when a save clears the BMI, and it needs review.
 */
export const isEstimatedBmiSource = (bmiSource?: ReferralBmiSource | null): boolean =>
  bmiSource === 'icd10' || bmiSource === 'stated_range' || bmiSource === 'stated_open_range';

/**
 * Whether the API in use re-derives a cleared BMI on save, told apart by whether it sends
 * `bmiSource` at all. leads-api 0.64.0 (#140) always sends the field, `null` included, and
 * re-derives; an older API omits it and stores a cleared BMI as sent. The one place the card
 * tells the two APIs apart.
 */
export const apiRederivesClearedBmi = (
  bmiSource: ReferralBmiSource | null | undefined,
): boolean => {
  switch (bmiSource) {
    case undefined:
      return false;
    default:
      return true;
  }
};

/**
 * Whether a loaded BMI should follow the measurements.
 *
 * leads-api 0.64.0 (#140) says how the stored BMI was obtained, and that is preferred:
 * - `stated`: the document or an operator gave it. Kept, even when it equals what height and
 *   weight give, as long as it goes back unchanged: leads-api saves a BMI changed to exactly
 *   what they give as derived.
 * - `measurements`: derived from height and weight, so it follows them while they give one.
 * - `icd10`, `stated_range`, `stated_open_range`: an estimate (`isEstimatedBmiSource`). It
 *   follows the measurements once they give a BMI, as the server does on save, and stands until
 *   then (`bmiFallback`).
 * - `null`: stored before 0.64.0, or no BMI. Inferred: it follows when nothing is stored or
 *   it equals what the measurements give. A stored `null` follows too: the server derives a
 *   cleared BMI again on save when it can, so keeping it empty here would only hide the BMI
 *   the save is about to store.
 *
 * With no `bmiSource` at all (an API before 0.64.0) the card behaves as it did: the same
 * inference, except that a stored `null` is a BMI someone cleared and stays cleared, since
 * that API stores the clear as sent.
 */
export const isBmiDerivedOnLoad = (
  state: Omit<MeasurementEditState, 'bmiDerived'>,
  storedBmi?: number | null,
  bmiSource?: ReferralBmiSource | null,
): boolean => {
  switch (bmiSource) {
    case 'stated':
      return false;
    case 'measurements':
      return bmiFromMeasurements(state) !== null;
    case 'icd10':
    case 'stated_range':
    case 'stated_open_range':
      return true;
    case null:
      return matchesMeasurements(state);
    case undefined:
      return storedBmi !== null && matchesMeasurements(state);
  }
};

/** What the referral card says about how a stored BMI was obtained, or `null` for none. */
export const bmiSourceNote = (
  bmiSource?: ReferralBmiSource | null,
): { text: string; needsReview: boolean } | null => {
  switch (bmiSource) {
    case 'measurements':
      return { text: 'Derived from height and weight', needsReview: false };
    case 'icd10':
      return { text: 'Estimated from ICD-10 code — needs review', needsReview: true };
    case 'stated_range':
      return {
        text: 'Midpoint of a BMI range in the document — needs review',
        needsReview: true,
      };
    case 'stated_open_range':
      return {
        text: 'Estimated from an open BMI bound (e.g. "50 or higher") — needs review',
        needsReview: true,
      };
    default:
      return null;
  }
};

const ESTIMATE_REASONS: Partial<Record<ReferralBmiSource, string>> = {
  icd10: 'the BMI is estimated from an ICD-10 code',
  stated_range: 'the BMI is the midpoint of a BMI range in the document',
  stated_open_range: 'the BMI is estimated from an open BMI bound in the document',
};

const RESOLVE_TO_KEEP = 'or mark the document as resolved from the Documents list to keep';

/**
 * Why leads-api flagged the referral's BMI for review (`bmiNeedsReview`).
 *
 * - A stated or measured BMI: leads-api (#140, decision of 2026-10-13) flags these only when the
 *   BMI is unusual for a referral, so the source alone says why; the range itself stays in
 *   leads-api. The way out is to correct it (for a measured BMI, the height and weight it came
 *   from), or to mark the document resolved, which keeps it; re-saving it unchanged keeps the flag.
 * - Otherwise the document gave neither a usable BMI nor a complete height and weight, and an
 *   estimate may have filled the BMI anyway. Re-saving an estimate unchanged keeps it an
 *   estimate and keeps the flag, so only a typed BMI or complete height and weight clear it.
 */
export const bmiReviewReason = (
  bmiSource?: ReferralBmiSource | null,
  bmi?: number | null,
): string => {
  const shown = typeof bmi === 'number' && Number.isFinite(bmi) ? ` (${bmi})` : '';
  if (bmiSource === 'stated') {
    return (
      `The BMI written in the document${shown} is unusual for a referral. Check it against the ` +
      `document, then correct it, ${RESOLVE_TO_KEEP} it.`
    );
  }
  if (bmiSource === 'measurements') {
    return (
      `The BMI from height and weight${shown} is unusual for a referral. Check the height and ` +
      `weight against the document, then correct them, ${RESOLVE_TO_KEEP} the BMI.`
    );
  }
  const estimate = bmiSource ? ESTIMATE_REASONS[bmiSource] : undefined;
  return estimate
    ? `No BMI or complete height and weight in the document: ${estimate}. ` +
        'Enter the BMI, or height and weight.'
    : 'No BMI or complete height and weight in the document. Enter the BMI, or height and weight.';
};

/**
 * What an emptied BMI field tells the operator the save will store, on an API that derives a
 * cleared BMI again (`apiRederivesClearedBmi`); `null` on an older one, which stores the clear.
 * leads-api re-applies its precedence to a cleared BMI: height and weight if they give one,
 * then the estimate the referral had, then whatever else the document gives.
 */
export const emptyBmiHint = (
  bmiSource: ReferralBmiSource | null | undefined,
  derived: number | null,
): string | null => {
  if (!apiRederivesClearedBmi(bmiSource)) {
    return null;
  }
  if (derived !== null) {
    return 'Left empty, the save derives it again.';
  }
  switch (bmiSource) {
    case 'icd10':
      return 'Left empty, the save restores the ICD-10 estimate.';
    case 'stated_range':
    case 'stated_open_range':
      return "Left empty, the save restores the estimate from the document's BMI.";
    default:
      return 'Left empty, the save derives a BMI from the document if it gives one.';
  }
};

/**
 * The measurement state after one input changes.
 *
 * - Height or weight: a following BMI follows them, and is cleared the moment the derivation
 *   stops being valid, so a BMI is never saved against measurements that no longer give it.
 *   An estimate (`bmiFallback`) comes back instead of the empty field. A BMI the
 *   operator typed is left alone.
 * - BMI: whatever is typed is kept and stops following the measurements, an empty field
 *   included. Reading empty as "derive again" refilled the field the instant its last
 *   character was deleted, so a BMI being retyped was replaced mid-edit, and a stored BMI
 *   could not be cleared while the measurements gave one.
 */
export const applyMeasurementEdit = <T extends MeasurementEditState>(
  state: T,
  key: ValidatedMeasurementKey,
  value: string,
): T => {
  if (key === 'bmi') {
    return { ...state, bmi: value, bmiDerived: false, bmiFallback: undefined };
  }

  const next = { ...state, [key]: value };
  if (!next.bmiDerived) {
    return next;
  }
  const derived = bmiFromMeasurements(next);
  return { ...next, bmi: derived === null ? (next.bmiFallback ?? '') : String(derived) };
};

/**
 * The explicit "Use calculated BMI" action: the BMI takes the value height and weight give, and
 * follows them again. Unchanged when they give none.
 */
export const followCalculatedBmi = <T extends MeasurementEditState>(state: T): T => {
  const derived = bmiFromMeasurements(state);
  return derived === null ? state : { ...state, bmi: String(derived), bmiDerived: true };
};
